import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  type BillingPreview, type BillingAccountRow, type BillingUserRow,
  type BillingWeeklyGroup, type BillingStaleDraftRow, type BillingSyncStatus,
  type BillingEnrichedWorklog,
  type BillingReconcileResult, type BillingReconcileAccountRow, type BillingReconcileIssue,
} from '../lib/api';
import { fmtMonth } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  ActionButton, Badge, Card, CardHeader, EmptyState, MetricCard, Modal, SectionLabel,
  SortableTh, Spinner, Table, Td, Th, Tr,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';
import { InvoiceLink } from '../components/InvoiceLink';
import { CreditAvailableBadge } from '../components/CreditAvailableBadge';
import { RecentImportsCard } from './AdminBillingRunsList';

/**
 * Tempo → ChargeOver billing preview. Read-only in this phase — the import
 * button is intentionally disabled until the Cloud Tasks worker lands in
 * the next phase. Drill-down + CSV exports + warning banners + invoice
 * date override are all here today so the workflow is usable end-to-end
 * for ops review before the writeback path goes live.
 */

type Tab = 'by-account' | 'by-user' | 'stale-drafts' | 'reconcile';

const fmtHours = (h: number) => `${h.toFixed(2)}h`;

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function lastCompleteMonthKey(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function AdminBillingPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState<string>(lastCompleteMonthKey());
  const [tab, setTab] = useState<Tab>('by-account');
  const [preview, setPreview] = useState<BillingPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [invoiceDate, setInvoiceDate] = useState<string>('');
  /** Account keys the operator has selected for import. Initialised to
   *  "all billable + all stale-drafts" when a preview lands, mirroring the
   *  legacy tool's default. */
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  /** Confirmation modal toggle. Click "Import" once → open the modal with
   *  a final summary; click "Import" inside the modal → fire the actual
   *  enqueue. Matches the legacy tool's window.confirm dialog. */
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [allowedMonths, setAllowedMonths] = useState<string[] | null>(null);
  const [defaultInvoiceDates, setDefaultInvoiceDates] = useState<Record<string, string>>({});
  if (allowedMonths === null) {
    api.admin.billing.months()
      .then(({ months, defaultInvoiceDates }) => {
        setAllowedMonths(months);
        setDefaultInvoiceDates(defaultInvoiceDates);
      })
      .catch(() => setAllowedMonths([currentMonthKey(), lastCompleteMonthKey()]));
  }

  async function runPreview() {
    setLoading(true);
    setError(null);
    setExpanded(null);
    setImportError(null);
    try {
      const data = await api.admin.billing.preview(month);
      setPreview(data);
      setInvoiceDate(data.defaultInvoiceDate);
      // Default-select every billable account + every stale draft. Same
      // default as the legacy tool's "Select all" behaviour.
      const next = new Set<string>();
      for (const a of data.byAccount) next.add(a.accountKey);
      for (const s of data.staleDraftInvoices) next.add(s.accountKey);
      setSelectedAccounts(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  function toggleAccount(accountKey: string) {
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountKey)) next.delete(accountKey);
      else next.add(accountKey);
      return next;
    });
  }
  function setAllAccounts(value: boolean) {
    if (!preview) return;
    if (!value) { setSelectedAccounts(new Set()); return; }
    const next = new Set<string>();
    for (const a of preview.byAccount) next.add(a.accountKey);
    for (const s of preview.staleDraftInvoices) next.add(s.accountKey);
    setSelectedAccounts(next);
  }
  function setNonReviewOnly() {
    if (!preview) return;
    const next = new Set<string>();
    for (const a of preview.byAccount) if (a.flaggedSeconds === 0) next.add(a.accountKey);
    setSelectedAccounts(next);
  }
  function setStaleDraftsOnly() {
    if (!preview) return;
    const next = new Set<string>();
    for (const s of preview.staleDraftInvoices) next.add(s.accountKey);
    setSelectedAccounts(next);
  }

  async function submitImport() {
    if (!preview) return;
    setConfirmOpen(false);
    setImporting(true);
    setImportError(null);
    try {
      const { runId } = await api.admin.billing.enqueueImport({
        month: preview.monthKey,
        invoiceDate,
        selectedAccounts: Array.from(selectedAccounts),
      });
      navigate(`/admin/billing/imports/${runId}`);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : String(err));
      setImporting(false);
    }
  }

  const flaggedAccounts = preview?.byAccount.filter((a) => a.flaggedSeconds > 0) ?? [];

  // Stale-draft count + capped-range flag are surfaced in the confirmation
  // modal so the operator sees exactly what the run will do at the moment
  // of submit, not just what the preview happened to show earlier.
  const staleSelected = preview
    ? preview.staleDraftInvoices.filter((s) => selectedAccounts.has(s.accountKey)).length
    : 0;

  return (
    <>
      {/* "Recent imports" is rendered first AND outside the preview block so
          ops can see what's run lately without first running a preview. */}
      <RecentImportsCard />

      <SectionLabel>Billing preview</SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Tempo → ChargeOver preview</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            Read-only pre-import snapshot. Mid-week runs cap at the most recent completed Sunday.
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                // Pre-fill the invoice-date input so a no-preview-yet user
                // sees the convention the system will use; the server
                // overwrites this once `runPreview` lands.
                if (defaultInvoiceDates[e.target.value]) {
                  setInvoiceDate(defaultInvoiceDates[e.target.value]);
                }
              }}
              style={selectStyle}
            >
              {(allowedMonths ?? [month]).map((m) => (
                <option key={m} value={m}>{fmtMonth(m)}</option>
              ))}
            </select>
            <ActionButton onClick={runPreview} disabled={loading} variant="primary">
              {loading ? 'Running…' : 'Run preview'}
            </ActionButton>
          </span>
        </CardHeader>

        {error && (
          <div style={{
            padding: '10px 16px', background: 'var(--color-red-bg)',
            color: 'var(--color-red-text)', fontSize: 12, borderTop: '0.5px solid var(--color-border)',
          }}>
            Preview failed: {error}
          </div>
        )}

        {preview?.range.capped && (
          <div style={{
            padding: '8px 16px', background: 'var(--color-amber-bg)',
            color: 'var(--color-amber-text)', fontSize: 11,
            borderTop: '0.5px solid var(--color-border)',
          }}>
            Range capped at {preview.range.capInfo?.cappedAt} (most recent completed Sunday)
            — full month ends {preview.range.capInfo?.rawMonthEnd}. Re-run after Sunday for full coverage.
          </div>
        )}
        {preview && <SyncStatusBanner status={preview.syncStatus} />}
      </Card>

      {!preview && !loading && (
        <EmptyState message="Pick a month and click Run preview." />
      )}
      {loading && <Spinner />}

      {preview && (
        <>
          {/* Prominent warning banners — both call out things the operator must
              acknowledge before submitting an import. Two separate banners
              (vs one combined) so each can carry its own action link. */}
          {flaggedAccounts.length > 0 && (
            <div style={warningBannerStyle('amber')}>
              <strong>⚠ {flaggedAccounts.length} account{flaggedAccounts.length === 1 ? '' : 's'} contain worklogs flagged for DM review</strong>
              <span style={{ marginLeft: 8 }}>
                Total flagged hours: {fmtHours(preview.totals.flaggedHours)}. Verify the flagged time
                with the relevant DM before including these accounts in an import.
              </span>
              <button
                type="button"
                onClick={() => setTab('by-account')}
                style={inlineLinkStyle('amber')}
              >
                Review by-account →
              </button>
            </div>
          )}
          {preview.staleDraftInvoices.length > 0 && (
            <div style={warningBannerStyle('amber')}>
              <strong>⚠ {preview.staleDraftInvoices.length} stale draft invoice{preview.staleDraftInvoices.length === 1 ? '' : 's'} detected</strong>
              <span style={{ marginLeft: 8 }}>
                Unapproved ChargeOver drafts for this month with no remaining billable time —
                typically a worklog was reclassified non-billable after the draft was created.
                Selecting these accounts in an import will zero them out.
              </span>
              <button
                type="button"
                onClick={() => setTab('stale-drafts')}
                style={inlineLinkStyle('amber')}
              >
                Show stale drafts →
              </button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
            <MetricCard
              label="Worklogs"
              value={preview.totals.worklogs}
              sub={`${preview.byAccount.length} accounts · ${preview.byUser.length} users`}
            />
            <MetricCard
              label="Total billable"
              value={fmtHours(preview.totals.hours)}
              accent="green"
              sub={`${preview.range.from} → ${preview.range.to}`}
            />
            <MetricCard
              label="Flagged for DM review"
              value={fmtHours(preview.totals.flaggedHours)}
              accent={preview.totals.flaggedHours > 0 ? 'amber' : undefined}
              sub={`${((preview.totals.flaggedSeconds / Math.max(preview.totals.seconds, 1)) * 100).toFixed(1)}% of total`}
            />
            <MetricCard
              label="Client engagements"
              value={preview.clientEngagementAccountKeys.length}
              sub="Tempo CLIENT_ENGAGEMENTS scope"
            />
          </div>

          {/* Invoice-date override + export controls. The date input is wired
              ready for the Phase 3 import button — the value will travel as
              the invoice `date` on each created ChargeOver invoice. */}
          <Card>
            <CardHeader>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Import controls</span>
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Invoice date</span>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    style={dateInputStyle}
                    title="Override the ChargeOver invoice date for every invoice in this batch. Default is month-end + 3 days."
                  />
                  {invoiceDate !== preview.defaultInvoiceDate && (
                    <button
                      type="button"
                      onClick={() => setInvoiceDate(preview.defaultInvoiceDate)}
                      style={{ fontSize: 10, color: 'var(--color-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                    >
                      reset
                    </button>
                  )}
                </label>
                <span style={{ borderLeft: '0.5px solid var(--color-border-strong)', height: 18 }} />
                <ActionButton
                  onClick={() => exportWorklogsCsv(preview)}
                  disabled={preview.enrichedWorklogs.length === 0}
                >
                  Worklogs CSV
                </ActionButton>
                <ActionButton
                  onClick={() => exportWeeklyCsv(preview)}
                  disabled={preview.weeklyGroupsByAccount.length === 0}
                >
                  Weekly CSV
                </ActionButton>
                <span style={{ borderLeft: '0.5px solid var(--color-border-strong)', height: 18 }} />
                <ActionButton
                  onClick={() => setConfirmOpen(true)}
                  disabled={
                    importing
                    || selectedAccounts.size === 0
                    || preview.syncStatus.isStale
                    || !/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)
                  }
                  variant="primary"
                >
                  {importing
                    ? 'Enqueuing…'
                    : `Import ${selectedAccounts.size} account${selectedAccounts.size === 1 ? '' : 's'} to ChargeOver`}
                </ActionButton>
              </span>
            </CardHeader>
            {importError && (
              <div style={{
                padding: '8px 16px', background: 'var(--color-red-bg)',
                color: 'var(--color-red-text)', fontSize: 11,
                borderTop: '0.5px solid var(--color-border)',
              }}>
                Failed to enqueue import: {importError}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader>
              <span style={{ display: 'flex', gap: 4 }}>
                <TabButton active={tab === 'by-account'} onClick={() => setTab('by-account')}>
                  By account ({preview.byAccount.length})
                </TabButton>
                <TabButton active={tab === 'by-user'} onClick={() => setTab('by-user')}>
                  By user ({preview.byUser.length})
                </TabButton>
                {preview.staleDraftInvoices.length > 0 && (
                  <TabButton active={tab === 'stale-drafts'} onClick={() => setTab('stale-drafts')} variant="amber">
                    Stale drafts ({preview.staleDraftInvoices.length})
                  </TabButton>
                )}
                <TabButton active={tab === 'reconcile'} onClick={() => setTab('reconcile')}>
                  Reconcile vs Tempo
                </TabButton>
              </span>
            </CardHeader>

            {tab === 'by-account' && (
              <>
                <SelectorStrip
                  selectedCount={selectedAccounts.size}
                  totalSelectable={preview.byAccount.length + preview.staleDraftInvoices.length}
                  onAll={() => setAllAccounts(true)}
                  onNone={() => setAllAccounts(false)}
                  onNonReview={setNonReviewOnly}
                  onStaleOnly={setStaleDraftsOnly}
                />
                <AccountTable
                  rows={preview.byAccount}
                  worklogs={preview.enrichedWorklogs}
                  weeklyByAccount={preview.weeklyGroupsByAccount}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  selectedAccounts={selectedAccounts}
                  onToggle={toggleAccount}
                />
              </>
            )}
            {tab === 'by-user' && (
              <UserTable
                rows={preview.byUser}
                worklogs={preview.enrichedWorklogs}
                expanded={expanded}
                setExpanded={setExpanded}
              />
            )}
            {tab === 'stale-drafts' && (
              <StaleDraftsTable
                rows={preview.staleDraftInvoices}
                selectedAccounts={selectedAccounts}
                onToggle={toggleAccount}
              />
            )}
            {tab === 'reconcile' && (
              <ReconcileTab key={month} month={month} />
            )}
          </Card>
        </>
      )}

      {/* Confirmation modal — fires only when the operator clicks Import.
          Mirrors the legacy tool's window.confirm summary: count, month,
          stale-draft count, capped-range note. Click "Import" inside to
          actually enqueue. */}
      {confirmOpen && preview && (
        <Modal
          title="Confirm import to ChargeOver"
          subtitle="This will create or update invoices in ChargeOver."
          width={520}
          onClose={() => setConfirmOpen(false)}
        >
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div>
              Import <strong>{selectedAccounts.size}</strong> account
              {selectedAccounts.size === 1 ? '' : 's'} into ChargeOver for{' '}
              <strong>{fmtMonth(preview.monthKey)}</strong>?
            </div>
            <div style={{ color: 'var(--color-text-muted)' }}>
              This will create new <strong>Unapproved</strong> invoices for accounts without one,
              or update existing Unapproved drafts where applicable. Invoice date:{' '}
              <strong>{invoiceDate}</strong>.
            </div>
            {staleSelected > 0 && (
              <div style={{
                background: 'var(--color-amber-bg)', color: 'var(--color-amber-text)',
                border: '0.5px solid var(--color-amber)',
                borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: 12,
              }}>
                <strong>{staleSelected}</strong> of these are stale drafts that will be{' '}
                <strong>zeroed out</strong>.
              </div>
            )}
            {preview.range.capped && (
              <div style={{
                background: 'var(--color-amber-bg)', color: 'var(--color-amber-text)',
                border: '0.5px solid var(--color-amber)',
                borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: 12,
              }}>
                The date range is capped at <strong>{preview.range.capInfo?.cappedAt}</strong>{' '}
                (last completed Sunday). The in-progress week will not be billed —
                the invoice date stays {invoiceDate}.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <ActionButton onClick={() => setConfirmOpen(false)}>Cancel</ActionButton>
              <ActionButton onClick={submitImport} variant="primary" disabled={importing}>
                {importing ? 'Enqueuing…' : `Import ${selectedAccounts.size}`}
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function SelectorStrip({
  selectedCount, totalSelectable, onAll, onNone, onNonReview, onStaleOnly,
}: {
  selectedCount: number;
  totalSelectable: number;
  onAll: () => void;
  onNone: () => void;
  onNonReview: () => void;
  onStaleOnly: () => void;
}) {
  return (
    <div style={{
      padding: '8px 16px', borderBottom: '0.5px solid var(--color-border)',
      display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginRight: 4 }}>
        {selectedCount} / {totalSelectable} selected
      </span>
      <ActionButton onClick={onAll}>Select all</ActionButton>
      <ActionButton onClick={onNone}>Select none</ActionButton>
      <ActionButton onClick={onNonReview}>All except review</ActionButton>
      <ActionButton onClick={onStaleOnly}>Stale drafts only</ActionButton>
    </div>
  );
}

// ─── CSV exports ────────────────────────────────────────────────────────────

function exportWorklogsCsv(p: BillingPreview): void {
  const rows: Array<Array<unknown>> = [[
    'worklog_id', 'date', 'user_name', 'user_id', 'account_key', 'account_name',
    'billable_seconds', 'billable_hours', 'flag_for_delivery_manager_review', 'description',
  ]];
  for (const wl of p.enrichedWorklogs) {
    rows.push([
      wl.tempoWorklogKey, wl.workDate ?? '',
      wl.authorName, wl.userId ?? '',
      wl.accountKey, wl.accountName ?? '',
      wl.billableSeconds, (wl.billableSeconds / 3600).toFixed(2),
      wl.isFlaggedForDMReview ? 'true' : 'false',
      wl.description,
    ]);
  }
  downloadCsv(`tempo_worklogs_${p.monthKey}.csv`, rows);
}

function exportWeeklyCsv(p: BillingPreview): void {
  const accountNameByKey = new Map(p.byAccount.map((a) => [a.accountKey, a.accountName]));
  const rows: Array<Array<unknown>> = [[
    'account_key', 'account_name', 'week_start', 'week_end',
    'billable_seconds', 'billable_hours', 'flagged_seconds', 'flagged_hours',
  ]];
  for (const { accountKey, groups } of p.weeklyGroupsByAccount) {
    const name = accountNameByKey.get(accountKey) ?? accountKey;
    for (const g of groups) {
      rows.push([
        accountKey, name, g.weekStart, g.weekEnd,
        g.totalSeconds, (g.totalSeconds / 3600).toFixed(2),
        g.flaggedSeconds, (g.flaggedSeconds / 3600).toFixed(2),
      ]);
    }
  }
  downloadCsv(`tempo_account_weekly_${p.monthKey}.csv`, rows);
}

// ─── Tab strip + banners ────────────────────────────────────────────────────

function TabButton({
  active, children, onClick, variant = 'blue',
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'blue' | 'amber';
}) {
  const accent = variant === 'amber' ? 'var(--color-amber)' : 'var(--color-blue)';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none', background: 'none', cursor: 'pointer',
        padding: '6px 10px', fontSize: 12, fontWeight: active ? 600 : 500,
        color: active ? accent : 'var(--color-text-muted)',
        borderBottom: active ? `2px solid ${accent}` : '2px solid transparent',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function SyncStatusBanner({ status }: { status: BillingSyncStatus }) {
  const bg = status.isStale ? 'var(--color-red-bg)' : 'var(--color-green-bg)';
  const fg = status.isStale ? 'var(--color-red-text)' : 'var(--color-green-text)';
  const label = status.lastSuccessfulSyncAt
    ? new Date(status.lastSuccessfulSyncAt).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : 'never';
  const ageLabel =
    status.minutesSinceLastSync == null ? 'no successful sync on record'
    : status.minutesSinceLastSync < 60 ? `${status.minutesSinceLastSync}m ago`
    : `${Math.round(status.minutesSinceLastSync / 60)}h ${status.minutesSinceLastSync % 60}m ago`;
  return (
    <div style={{
      padding: '8px 16px', background: bg, color: fg, fontSize: 11,
      borderTop: '0.5px solid var(--color-border)',
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    }}>
      <strong>Last successful FULL sync:</strong> {label} ({ageLabel}).
      {status.isStale
        ? <span> &nbsp;Older than the {status.thresholdMinutes}-minute threshold — imports will be blocked until the next full sync (03:30 UTC daily, or trigger manually from Data sync).</span>
        : <span> &nbsp;Within the {status.thresholdMinutes}-minute freshness window. Incremental syncs don't count.</span>}
    </div>
  );
}

function warningBannerStyle(variant: 'amber' | 'red'): React.CSSProperties {
  const bg = variant === 'red' ? 'var(--color-red-bg)' : 'var(--color-amber-bg)';
  const fg = variant === 'red' ? 'var(--color-red-text)' : 'var(--color-amber-text)';
  const border = variant === 'red' ? 'var(--color-red)' : 'var(--color-amber)';
  return {
    padding: '10px 14px', background: bg, color: fg,
    border: `0.5px solid ${border}`, borderLeft: `3px solid ${border}`,
    borderRadius: 'var(--radius-md)', fontSize: 12,
    display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap',
  };
}

function inlineLinkStyle(variant: 'amber' | 'red'): React.CSSProperties {
  const fg = variant === 'red' ? 'var(--color-red-text)' : 'var(--color-amber-text)';
  return {
    marginLeft: 'auto', background: 'none', border: 'none',
    color: fg, fontSize: 11, fontWeight: 600, cursor: 'pointer',
    textDecoration: 'underline', padding: '2px 4px',
  };
}

const selectStyle: React.CSSProperties = {
  padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius-sm)',
  border: '0.5px solid var(--color-border-strong)',
  background: 'var(--color-surface)', color: 'var(--color-text)',
};

const dateInputStyle: React.CSSProperties = {
  padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius-sm)',
  border: '0.5px solid var(--color-border-strong)',
  background: 'var(--color-surface)', color: 'var(--color-text)',
};

// ─── Account + user tables with drill-downs ─────────────────────────────────

function AccountTable({
  rows, worklogs, weeklyByAccount, expanded, setExpanded,
  selectedAccounts, onToggle,
}: {
  rows: BillingAccountRow[];
  worklogs: BillingEnrichedWorklog[];
  weeklyByAccount: Array<{ accountKey: string; groups: BillingWeeklyGroup[] }>;
  expanded: string | null;
  setExpanded: (next: string | null) => void;
  selectedAccounts: Set<string>;
  onToggle: (accountKey: string) => void;
}) {
  const { sorted, sort } = useTableSort(rows, {
    account: (r: BillingAccountRow) => r.accountName,
    lead: (r: BillingAccountRow) => r.leadDisplayName,
    worklogs: (r: BillingAccountRow) => r.worklogCount,
    hours: (r: BillingAccountRow) => r.totalSeconds,
    flagged: (r: BillingAccountRow) => r.flaggedSeconds,
  }, { key: 'account' });
  if (rows.length === 0) return <EmptyState message="No billable time in this range." />;
  const weeklyMap = new Map(weeklyByAccount.map((w) => [w.accountKey, w.groups]));
  return (
    <Table>
      <thead>
        <tr>
          <Th> </Th>
          <SortableTh columnKey="account"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Account</SortableTh>
          <SortableTh columnKey="lead"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Lead</SortableTh>
          <SortableTh columnKey="worklogs" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Worklogs</SortableTh>
          <SortableTh columnKey="hours"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Hours</SortableTh>
          <SortableTh columnKey="flagged"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Flagged hours</SortableTh>
          <Th>Status</Th>
          <Th> </Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const key = `account:${r.accountKey}`;
          const isOpen = expanded === key;
          const isSelected = selectedAccounts.has(r.accountKey);
          const weekly = weeklyMap.get(r.accountKey) ?? [];
          const accountWorklogs = worklogs
            .filter((w) => w.accountKey === r.accountKey)
            .sort((a, b) => (a.workDate ?? '').localeCompare(b.workDate ?? ''));
          return (
            <>
              <Tr
                key={key}
                onClick={() => setExpanded(isOpen ? null : key)}
                selected={isOpen}
                style={r.flaggedSeconds > 0 ? { background: 'var(--color-amber-bg)' } : undefined}
              >
                <Td>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggle(r.accountKey)}
                  />
                </Td>
                <Td bold>
                  {r.accountName}
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 10, marginLeft: 6 }}>
                    {r.accountKey}
                  </span>
                </Td>
                <Td muted style={{ fontSize: 11 }}>{r.leadDisplayName ?? '—'}</Td>
                <Td right>{r.worklogCount}</Td>
                <Td right bold>{fmtHours(r.totalHours)}</Td>
                <Td right style={{ color: r.flaggedSeconds > 0 ? 'var(--color-amber)' : undefined }}>
                  {r.flaggedSeconds > 0 ? fmtHours(r.flaggedHours) : '—'}
                </Td>
                <Td>{r.flaggedSeconds > 0 ? <Badge variant="amber">Review</Badge> : null}</Td>
                <Td muted style={{ fontSize: 10 }}>{isOpen ? 'Details ↑' : 'Details ↓'}</Td>
              </Tr>
              {isOpen && (
                <tr key={`${key}-detail`}>
                  <td colSpan={8} style={{ padding: '8px 16px 14px', background: 'var(--color-bg)' }}>
                    <WeeklyBreakdownStrip weekly={weekly} />
                    <WorklogCards rows={accountWorklogs} showField="user" />
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </Table>
  );
}

function UserTable({
  rows, worklogs, expanded, setExpanded,
}: {
  rows: BillingUserRow[];
  worklogs: BillingEnrichedWorklog[];
  expanded: string | null;
  setExpanded: (next: string | null) => void;
}) {
  const { sorted, sort } = useTableSort(rows, {
    user: (r: BillingUserRow) => r.authorName,
    userId: (r: BillingUserRow) => r.userId,
    worklogs: (r: BillingUserRow) => r.worklogCount,
    hours: (r: BillingUserRow) => r.totalSeconds,
    flagged: (r: BillingUserRow) => r.flaggedSeconds,
  }, { key: 'hours', dir: 'desc' });
  if (rows.length === 0) return <EmptyState message="No billable time in this range." />;
  return (
    <Table>
      <thead>
        <tr>
          <SortableTh columnKey="user"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>User</SortableTh>
          <SortableTh columnKey="userId"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>User ID</SortableTh>
          <SortableTh columnKey="worklogs" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Worklogs</SortableTh>
          <SortableTh columnKey="hours"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Hours</SortableTh>
          <SortableTh columnKey="flagged"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Flagged hours</SortableTh>
          <Th> </Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const key = `user:${r.userId ?? r.authorName}`;
          const isOpen = expanded === key;
          const userWorklogs = worklogs
            .filter((w) => (w.userId ?? '') === (r.userId ?? '') && w.authorName === r.authorName)
            .sort((a, b) => (a.workDate ?? '').localeCompare(b.workDate ?? ''));
          return (
            <>
              <Tr
                key={key}
                onClick={() => setExpanded(isOpen ? null : key)}
                selected={isOpen}
                style={r.flaggedSeconds > 0 ? { background: 'var(--color-amber-bg)' } : undefined}
              >
                <Td bold>{r.authorName}</Td>
                <Td muted style={{ fontSize: 10 }}>{r.userId ?? '—'}</Td>
                <Td right>{r.worklogCount}</Td>
                <Td right bold>{fmtHours(r.totalHours)}</Td>
                <Td right style={{ color: r.flaggedSeconds > 0 ? 'var(--color-amber)' : undefined }}>
                  {r.flaggedSeconds > 0 ? fmtHours(r.flaggedHours) : '—'}
                </Td>
                <Td muted style={{ fontSize: 10 }}>{isOpen ? 'Details ↑' : 'Details ↓'}</Td>
              </Tr>
              {isOpen && (
                <tr key={`${key}-detail`}>
                  <td colSpan={6} style={{ padding: '8px 16px 14px', background: 'var(--color-bg)' }}>
                    <WorklogCards rows={userWorklogs} showField="account" />
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </Table>
  );
}

function StaleDraftsTable({
  rows, selectedAccounts, onToggle,
}: {
  rows: BillingStaleDraftRow[];
  selectedAccounts: Set<string>;
  onToggle: (accountKey: string) => void;
}) {
  const { sorted, sort } = useTableSort(rows, {
    account: (r: BillingStaleDraftRow) => r.accountName,
    invoice: (r: BillingStaleDraftRow) => r.invoiceNumber ?? r.chargeoverInvoiceId,
    status:  (r: BillingStaleDraftRow) => r.rawStatus,
    total:   (r: BillingStaleDraftRow) => r.totalAmount,
  }, { key: 'account' });
  if (rows.length === 0) return <EmptyState message="No stale Unapproved drafts." />;
  return (
    <Table>
      <thead>
        <tr>
          <Th> </Th>
          <SortableTh columnKey="account" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Account</SortableTh>
          <SortableTh columnKey="invoice" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Invoice #</SortableTh>
          <SortableTh columnKey="status"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
          <SortableTh columnKey="total"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Current total</SortableTh>
          <Th>External key</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const isSelected = selectedAccounts.has(r.accountKey);
          return (
            <Tr
              key={r.chargeoverInvoiceId}
              style={{ background: 'var(--color-amber-bg)' }}
            >
              <Td>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggle(r.accountKey)}
                />
              </Td>
              <Td bold>
                {r.accountName}
                <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 10, marginLeft: 6 }}>
                  {r.accountKey}
                </span>
                <CreditAvailableBadge
                  unappliedCredit={r.customerUnappliedCredit}
                  pendingDeposit={r.customerPendingDeposit}
                />
              </Td>
              <Td muted style={{ fontSize: 11 }}>
                <InvoiceLink
                  invoiceId={r.chargeoverInvoiceId}
                  display={r.invoiceNumber ?? r.chargeoverInvoiceId}
                />
              </Td>
              <Td><Badge variant="amber">{r.rawStatus}</Badge></Td>
              <Td right>{r.totalAmount.toFixed(2)}</Td>
              <Td muted style={{ fontSize: 10 }}>{r.externalKey}</Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function WeeklyBreakdownStrip({ weekly }: { weekly: BillingWeeklyGroup[] }) {
  if (weekly.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
        Weekly breakdown
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {weekly.map((w) => (
          <div
            key={w.weekStart}
            style={{
              border: '0.5px solid var(--color-border)',
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-sm)', padding: '6px 10px',
              fontSize: 11, minWidth: 140,
            }}
          >
            <div style={{ color: 'var(--color-text-muted)' }}>{w.weekStart} → {w.weekEnd}</div>
            <div style={{ fontWeight: 600, marginTop: 2 }}>
              {fmtHours(Math.ceil((w.totalSeconds / 3600) * 4) / 4)}
            </div>
            {w.flaggedSeconds > 0 && (
              <div style={{ color: 'var(--color-amber)', fontSize: 10, marginTop: 2 }}>
                {fmtHours(Math.ceil((w.flaggedSeconds / 3600) * 4) / 4)} flagged
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WorklogCards({
  rows, showField,
}: {
  rows: BillingEnrichedWorklog[];
  /** Which contextual field to render on each card — "user" when drilling
   *  into an account row (the account is fixed; user varies), "account"
   *  vice versa. */
  showField: 'user' | 'account';
}) {
  if (rows.length === 0) {
    return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>No worklogs found.</span>;
  }
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map((wl) => (
        <div
          key={wl.tempoWorklogKey}
          style={{
            border: '0.5px solid var(--color-border)',
            borderLeft: wl.isFlaggedForDMReview ? '3px solid var(--color-amber)' : '0.5px solid var(--color-border)',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-sm)', padding: '8px 10px',
            fontSize: 11,
          }}
        >
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <span><strong>Date:</strong> {wl.workDate ?? '—'}</span>
            {showField === 'user' && <span><strong>User:</strong> {wl.authorName}</span>}
            {showField === 'account' && <span><strong>Account:</strong> {wl.accountName ?? wl.accountKey}</span>}
            <span><strong>Hours:</strong> {(wl.billableSeconds / 3600).toFixed(2)}h</span>
            {wl.isFlaggedForDMReview && <Badge variant="amber">Flagged for review</Badge>}
            <span style={{ marginLeft: 'auto', color: 'var(--color-text-muted)', fontSize: 10 }}>
              ID {wl.tempoWorklogKey}
            </span>
          </div>
          {wl.description && (
            <div style={{ marginTop: 4, color: 'var(--color-text-muted)' }}>{wl.description}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Reconcile vs Tempo ───────────────────────────────────────────────────────
// Upload the Tempo "Logged Time" report (.xlsx, grouped by worklog) and diff it
// against ORP's billable worklogs for the selected month. The backend re-runs the
// preview, so the comparison always reflects what ORP would actually bill.

const fmtSignedHours = (h: number) => `${h > 0 ? '+' : ''}${h.toFixed(2)}h`;
const optHours = (sec: number | null) => (sec == null ? '—' : `${(sec / 3600).toFixed(2)}h`);

function deltaColor(seconds: number): string {
  if (seconds > 0) return 'var(--color-red-text)';   // ORP over-counts
  if (seconds < 0) return 'var(--color-amber-text)'; // ORP under-counts
  return 'var(--color-text-muted)';
}

function ReconcileTab({ month }: { month: string }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BillingReconcileResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  async function run() {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setOpen(null);
    try {
      const buf = await file.arrayBuffer();
      setResult(await api.admin.billing.reconcile(month, buf));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Export Tempo's <strong>Logged Time</strong> report for <strong>{fmtMonth(month)}</strong> grouped by
        worklog (Account, Billable, and Tempo Worklog ID columns enabled), then upload the <code>.xlsx</code> here.
        ORP's billable worklogs are diffed against it by Tempo worklog ID — no billing data is changed.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setResult(null);
            setError(null);
          }}
          style={{ fontSize: 11 }}
        />
        <ActionButton onClick={run} variant="primary" disabled={!file || loading}>
          {loading ? 'Reconciling…' : 'Reconcile against Tempo'}
        </ActionButton>
      </div>

      {error && (
        <div
          style={{
            marginTop: 12, padding: '8px 12px', fontSize: 11, lineHeight: 1.5,
            background: 'var(--color-red-bg)', color: 'var(--color-red-text)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {error}
        </div>
      )}

      {loading && <div style={{ marginTop: 16 }}><Spinner /></div>}

      {result && !loading && (
        <ReconcileResultView result={result} open={open} setOpen={setOpen} />
      )}
    </div>
  );
}

function ReconcileResultView({
  result, open, setOpen,
}: {
  result: BillingReconcileResult;
  open: string | null;
  setOpen: (k: string | null) => void;
}) {
  const t = result.totals;
  const agree = result.accounts.length === 0;

  return (
    <div style={{ marginTop: 16 }}>
      {/* Headline banner */}
      <div
        style={{
          display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'baseline',
          padding: '12px 14px', marginBottom: 14, fontSize: 12,
          borderRadius: 'var(--radius-sm)',
          background: t.deltaSeconds === 0 ? 'var(--color-green-bg)' : 'var(--color-amber-bg)',
        }}
      >
        <span>ORP <strong>{fmtHours(t.orpHours)}</strong> <span style={{ color: 'var(--color-text-muted)' }}>({t.orpWorklogs} wl)</span></span>
        <span>Tempo <strong>{fmtHours(t.tempoHours)}</strong> <span style={{ color: 'var(--color-text-muted)' }}>({t.tempoWorklogs} wl)</span></span>
        <span>Δ <strong style={{ color: deltaColor(t.deltaSeconds) }}>{fmtSignedHours(t.deltaHours)}</strong></span>
        <span style={{ color: 'var(--color-text-muted)' }}>
          {agree ? 'all accounts agree' : `${result.accounts.length} account(s) differ`}
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--color-text-muted)', fontSize: 11 }}>
          matched {t.matched} · ORP-only {t.orpOnly} · Tempo-only {t.tempoOnly} · hrs differ {t.hoursDiffer} · acct differ {t.accountDiffer}
        </span>
      </div>

      {t.tempoOutsideWindow > 0 && (
        <div style={{ fontSize: 10.5, color: 'var(--color-text-muted)', marginBottom: 12 }}>
          {t.tempoOutsideWindow} Tempo worklog(s) dated after the billing cut-off ({result.range.to}) were set aside — ORP
          doesn't bill them yet, so they aren't counted as missing.
        </div>
      )}

      {agree ? (
        <EmptyState message="No differences — ORP billing matches the Tempo report for this month." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Account</Th>
              <Th right>ORP</Th>
              <Th right>Tempo</Th>
              <Th right>Δ</Th>
              <Th>Discrepancies</Th>
            </tr>
          </thead>
          <tbody>
            {result.accounts.map((a) => {
              const isOpen = open === a.accountKey;
              return (
                <Fragment key={a.accountKey}>
                  <Tr>
                    <Td>
                      <button
                        onClick={() => setOpen(isOpen ? null : a.accountKey)}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          font: 'inherit', color: 'inherit', textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 9 }}>{isOpen ? '▼' : '▶'}</span>
                        <span>
                          <span style={{ fontWeight: 500 }}>{a.accountName || a.accountKey}</span>
                          <span style={{ color: 'var(--color-text-muted)', fontSize: 10, marginLeft: 6 }}>{a.accountKey}</span>
                        </span>
                      </button>
                    </Td>
                    <Td right>{fmtHours(a.orpHours)}</Td>
                    <Td right>{fmtHours(a.tempoHours)}</Td>
                    <Td right style={{ color: deltaColor(a.deltaSeconds), fontWeight: 500 }}>{fmtSignedHours(a.deltaHours)}</Td>
                    <Td><AccountIssueBadges issues={a.issues} /></Td>
                  </Tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} style={{ padding: '0 16px 14px 34px', background: 'var(--color-bg)' }}>
                        <AccountDrilldown issues={a.issues} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function AccountIssueBadges({ issues }: { issues: BillingReconcileAccountRow['issues'] }) {
  const parts = ([
    { n: issues.orpOnly.length, label: 'ORP-only', variant: 'red' },
    { n: issues.tempoOnly.length, label: 'Tempo-only', variant: 'amber' },
    { n: issues.hoursDiffer.length, label: 'hrs differ', variant: 'blue' },
    { n: issues.accountDiffer.length, label: 'acct differ', variant: 'gray' },
  ] as Array<{ n: number; label: string; variant: 'red' | 'amber' | 'blue' | 'gray' }>).filter((p) => p.n > 0);
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {parts.map((p) => (
        <Badge key={p.label} variant={p.variant}>{p.n} {p.label}</Badge>
      ))}
    </span>
  );
}

function AccountDrilldown({ issues }: { issues: BillingReconcileAccountRow['issues'] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 12 }}>
      <IssueList
        title="In ORP, gone from Tempo — phantom / over-count"
        color="var(--color-red-text)"
        rows={issues.orpOnly}
      />
      <IssueList
        title="In Tempo, missing from ORP — under-count"
        color="var(--color-amber-text)"
        rows={issues.tempoOnly}
      />
      <IssueList
        title="Same worklog, hours differ"
        color="var(--color-blue)"
        rows={issues.hoursDiffer}
      />
      <IssueList
        title="Same worklog, attributed to a different account"
        color="var(--color-text-muted)"
        rows={issues.accountDiffer}
        showAccount
      />
    </div>
  );
}

function IssueList({
  title, color, rows, showAccount = false,
}: {
  title: string;
  color: string;
  rows: BillingReconcileIssue[];
  showAccount?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <div>
      <SectionLabel>{title} ({rows.length})</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
        {rows.map((r) => (
          <div key={r.worklogId} style={{ fontSize: 11, lineHeight: 1.45 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 10 }}>{r.workDate}</span>
              <span>{r.author || '—'}</span>
              <span style={{ color, fontWeight: 500 }}>
                ORP {optHours(r.orpSeconds)} · Tempo {optHours(r.tempoSeconds)}
              </span>
              {showAccount && r.orpAccountKey && (
                <span style={{ color: 'var(--color-text-muted)' }}>
                  ORP → {r.orpAccountKey}, Tempo → {r.accountKey}
                </span>
              )}
              <span style={{ marginLeft: 'auto', color: 'var(--color-text-muted)', fontSize: 10 }}>ID {r.worklogId}</span>
            </div>
            {r.description && (
              <div style={{ color: 'var(--color-text-muted)', marginTop: 2 }}>{r.description}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
